import * as React from 'react';
import {format, setHours, setMinutes} from 'date-fns';
import {cs} from 'date-fns/locale';
import {Calendar as CalendarIcon} from 'lucide-react';
import {clsx} from 'clsx';
import {ControllerRenderProps} from 'react-hook-form';

import {Button} from '../shadcn-components/button';
import {Calendar} from '../shadcn-components/calendar';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '../shadcn-components/popover';

type DatePickerProps = {
	field?: ControllerRenderProps<any, any>;
	onChange?: (val: string) => void;
	error?: string;
	text: string;
	value?: string;
	className?: string;
	hasHours?: boolean;
};

export function DatePicker({
	field,
	onChange,
	value,
	error,
	text,
	className,
	hasHours = false,
}: DatePickerProps) {
	const [open, setOpen] = React.useState(false);

	const isControlled = !!field || (!!onChange && !!value);
	const initialValue = field?.value ?? value;

	const [date, setDate] = React.useState<Date | undefined>(
		initialValue ? new Date(initialValue) : undefined
	);

	const [hours, setHoursState] = React.useState(date?.getHours() ?? 12);
	const [minutes, setMinutesState] = React.useState(date?.getMinutes() ?? 0);

	React.useEffect(() => {
		if (date) {
			const updated = setMinutes(setHours(date, hours), minutes);
			if (field) {
				field.onChange(updated.toISOString());
			} else if (onChange) {
				onChange(updated.toISOString());
			}
		}
	}, [
		hours,
		minutes
	]);

	const handleDateChange = (selectedDate: Date | undefined) => {
		if (!selectedDate) {
			return;
		}

		const updatedDate = setMinutes(setHours(selectedDate, hours), minutes);
		setDate(updatedDate);

		if (field) {
			field.onChange(updatedDate.toISOString());
		} else if (onChange) {
			onChange(updatedDate.toISOString());
		}

		setOpen(false);
	};

	return (
		<div className={clsx('space-y-2 w-full z-10', className)}>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outlined"
						data-empty={!date}
						className="data-[empty=true]:text-muted-foreground w-full h-[56px] justify-between text-left font-normal normal-case"
					>
						{date ? format(date, 'PPP HH:mm', {locale: cs}) : <span>{text}</span>}
						<CalendarIcon className="mr-2 h-4 w-4"/>
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-4 space-y-4 bg-white">
					<Calendar
						mode="single"
						selected={date}
						onSelect={handleDateChange}
					/>
					{hasHours &&
						<div className="flex items-center justify-center gap-2 mx-auto">
							<input
								type="number"
								min={0}
								max={23}
								value={hours}
								onChange={(e) => setHoursState(Number(e.target.value))}
								className="border rounded px-2 py-1 w-[60px]"
							/>
							<label className="text-sm">:</label>
							<input
								type="number"
								min={0}
								max={59}
								value={minutes}
								onChange={(e) => setMinutesState(Number(e.target.value))}
								className="border rounded px-2 py-1 w-[60px]"
							/>
						</div>
					}
				</PopoverContent>
			</Popover>
			{error && <p className="mt-1 text-xs text-red-700">{error}</p>}
		</div>
	);
}
